var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { css, html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { DefaultFftSize } from "./constants.js";
import { SpectrumHighlightChangedEvent } from "./events.js";
import { DragController } from "../controls/drag-controller.js";
import { Mapping } from "../coordinates/mapping.js";
import { DefaultZoom, Zoom } from "../coordinates/zoom.js";
let RrHighlight = class RrHighlight extends LitElement {
    constructor() {
        super(...arguments);
        this.draggablePoint = false;
        this.draggableLeft = false;
        this.draggableRight = false;
        this.fftSize = DefaultFftSize;
        this.zoom = DefaultZoom;
    }
    static get styles() {
        return [
            css `
        :host {
          pointer-events: none;
        }

        .handle {
          pointer-events: all;
        }

        #point,
        #band,
        .handle {
          position: absolute;
          top: 0;
          bottom: 0;
        }

        #point {
          width: 2px;
          background: var(--rr-highlight-color, rgba(255, 255, 0, 0.25));
        }

        #band {
          background: var(--rr-highlight-area-color, rgba(255, 255, 255, 0.25));
        }

        .handle {
          width: 4px;
          cursor: ew-resize;
        }

        #pointHandle {
          cursor: col-resize;
        }

        #pointHandle:hover {
          background: var(--rr-highlight-handle-color, rgba(255, 255, 0, 1));
        }

        #leftBandHandle:hover,
        #rightBandHandle:hover {
          background: var(
            --rr-highlight-area-handle-color,
            rgba(255, 255, 255, 1)
          );
        }

        .touchArea {
          position: absolute;
          top: 0;
          bottom: 0;
          left: -10px;
          right: -10px;
        }
      `,
        ];
    }
    render() {
        return html `${this.renderBand()}${this.renderPoint()}`;
    }
    renderPoint() {
        if (this.selection?.point === undefined)
            return nothing;
        const mapping = new Mapping(this.zoom, this.offsetWidth, this.fftSize);
        let c = mapping.zoomed(this.selection.point);
        if (c < 0 || c > 1)
            return nothing;
        return html `<div id="point" style="left:calc(${100 * c}% - 1px)"></div>
      ${this.draggablePoint
            ? html `<div
            id="pointHandle"
            class="handle"
            style="left:calc(${100 * c}% - 2px)"
            @pointerdown=${this.onPointPointerDown}
          >
            <div class="touchArea"></div>
          </div>`
            : nothing}`;
    }
    renderBand() {
        if (this.selection?.band === undefined)
            return nothing;
        const mapping = new Mapping(this.zoom, this.offsetWidth, this.fftSize);
        let l = mapping.zoomed(this.selection.band.left);
        let r = mapping.zoomed(this.selection.band.right);
        if (l > 1 || r < 0)
            return nothing;
        let le = Math.max(0, l);
        let re = Math.min(r, 1);
        return html `<div
        id="band"
        style="left:${100 * le}%;width:${100 * (re - le)}%"
      ></div>
      ${this.draggableLeft && l == le
            ? html `<div
            id="leftBandHandle"
            class="handle"
            style="left:calc(${100 * l}% - 2px)"
            @pointerdown=${this.onLeftPointerDown}
          >
            <div class="touchArea"></div>
          </div>`
            : nothing}${this.draggableRight && r == re
            ? html `<div
            id="rightBandHandle"
            class="handle"
            style="left:calc(${100 * r}% - 2px)"
            @pointerdown=${this.onRightPointerDown}
          >
            <div class="touchArea"></div>
          </div>`
            : nothing}`;
    }
    firstUpdated(changed) {
        super.firstUpdated(changed);
        this.pointDragController = new DragController(new HighlightDragHandler("point", this), 0);
        this.leftDragController = new DragController(new HighlightDragHandler("start", this), 0);
        this.rightDragController = new DragController(new HighlightDragHandler("end", this), 0);
    }
    onPointPointerDown(e) {
        this.pointDragController?.startDragging(e);
    }
    onLeftPointerDown(e) {
        this.leftDragController?.startDragging(e);
    }
    onRightPointerDown(e) {
        this.rightDragController?.startDragging(e);
    }
};
__decorate([
    property({ type: Boolean, reflect: true, attribute: "draggable-point" }),
    __metadata("design:type", Boolean)
], RrHighlight.prototype, "draggablePoint", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "draggable-left" }),
    __metadata("design:type", Boolean)
], RrHighlight.prototype, "draggableLeft", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "draggable-right" }),
    __metadata("design:type", Boolean)
], RrHighlight.prototype, "draggableRight", void 0);
__decorate([
    property({ type: Number, reflect: true }),
    __metadata("design:type", Number)
], RrHighlight.prototype, "fftSize", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", typeof (_a = typeof Zoom !== "undefined" && Zoom) === "function" ? _a : Object)
], RrHighlight.prototype, "zoom", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Object)
], RrHighlight.prototype, "selection", void 0);
RrHighlight = __decorate([
    customElement("rr-highlight")
], RrHighlight);
export { RrHighlight };
class HighlightDragHandler {
    constructor(type, highlight) {
        this.type = type;
        this.highlight = highlight;
    }
    startDrag() {
        this.original = this.highlight.selection;
    }
    drag(deltaX, _) {
        const zoom = this.highlight.zoom === undefined ? 1 : this.highlight.zoom.level;
        let fraction = this.getFraction();
        if (fraction !== undefined) {
            fraction += deltaX / (this.highlight.offsetWidth * zoom);
            if (fraction < 0)
                fraction = 0;
            if (fraction > 1)
                fraction = 1;
            this.highlight.dispatchEvent(this.getEvent(fraction));
        }
    }
    finishDrag() { }
    cancelDrag() {
        let fraction = this.getFraction();
        if (fraction !== undefined) {
            this.highlight.dispatchEvent(this.getEvent(fraction));
        }
    }
    onClick() { }
    getFraction() {
        return this.type == "point"
            ? this.original?.point
            : this.type == "start"
                ? this.original?.band?.left
                : this.original?.band?.right;
    }
    getEvent(fraction) {
        return new SpectrumHighlightChangedEvent(this.type == "point"
            ? { fraction }
            : this.type == "start"
                ? { startFraction: fraction }
                : { endFraction: fraction });
    }
}
